import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import BottomNav from '../components/BottomNav';

interface Reservation {
  id: string;
  guest_name: string | null;
  guest_phone: string | null;
  party_size: number;
  reservation_at: string;
  status: string;
  table_number?: number | null;
  notes?: string | null;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('fr-MA', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

const STATUS_ORDER = ['pending', 'confirmed', 'seated', 'cancelled', 'no_show'] as const;
const STATUS_LABEL: Record<string, string> = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  seated: 'Installée',
  cancelled: 'Annulée',
  no_show: 'No-show',
};

async function callReservationsList(
  storeId: string,
  date: string,
  action?: { id: string; new_status: string },
) {
  try {
    const { data, error } = await supabase.functions.invoke('tapnpay-reservations-list', {
      body: { store_id: storeId, date, action },
    });
    if (error) throw error;
    return data as { reservations?: Reservation[] };
  } catch {
    return null;
  }
}

export default function ReservationsPage() {
  const { session } = useAuth();
  const today = toISODate(new Date());
  const [date, setDate] = useState(today);
  const [items, setItems] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    if (!session?.storeId) return;
    setLoading(true);
    const fromEdge = await callReservationsList(session.storeId, date);
    if (fromEdge?.reservations) {
      setItems(fromEdge.reservations);
      setLoading(false);
      return;
    }

    // Fallback: direct query
    const startIso = new Date(`${date}T00:00:00`).toISOString();
    const endIso = new Date(`${date}T23:59:59`).toISOString();
    const { data } = await supabase
      .from('reservations')
      .select(
        'id, guest_name, guest_phone, party_size, reservation_at, status, table_number, notes',
      )
      .eq('store_id', session.storeId)
      .gte('reservation_at', startIso)
      .lte('reservation_at', endIso)
      .order('reservation_at');
    setItems((data as Reservation[] | null) ?? []);
    setLoading(false);
  }, [session?.storeId, date]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const updateStatus = async (id: string, newStatus: string) => {
    setBusy(id);
    try {
      const edge = await callReservationsList(session?.storeId ?? '', date, {
        id,
        new_status: newStatus,
      });
      if (!edge) {
        await supabase.from('reservations').update({ status: newStatus }).eq('id', id);
      }
      await fetchItems();
    } finally {
      setBusy(null);
    }
  };

  const grouped: Record<string, Reservation[]> = {};
  for (const r of items) {
    const key = r.status ?? 'pending';
    grouped[key] = grouped[key] ?? [];
    grouped[key].push(r);
  }

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
          Réservations
        </p>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="text-xs min-h-[36px] px-2 bg-surface text-on-surface tabular-nums"
          style={{ border: '0.5px solid #e5e7eb' }}
        />
      </header>

      {loading ? (
        <div className="p-6 text-center text-sm text-on-surface-variant">…</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <span className="material-symbols-outlined text-[48px] text-on-surface-variant">
            event_busy
          </span>
          <p className="text-sm text-on-surface-variant">
            Aucune réservation pour cette date
          </p>
        </div>
      ) : (
        <div className="p-3 space-y-4">
          {STATUS_ORDER.map((status) => {
            const rows = grouped[status] ?? [];
            if (rows.length === 0) return null;
            return (
              <section key={status}>
                <div className="px-1 mb-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    {STATUS_LABEL[status] ?? status}
                  </span>
                  <span className="text-[10px] text-on-surface-variant tabular-nums">
                    {rows.length}
                  </span>
                </div>
                <div className="bg-surface" style={{ border: '0.5px solid #e5e7eb' }}>
                  {rows.map((r, i) => (
                    <div
                      key={r.id}
                      className="px-4 py-3 flex flex-col gap-2"
                      style={{
                        borderBottom: i < rows.length - 1 ? '0.5px solid #e5e7eb' : undefined,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-on-surface-strong">
                            {r.guest_name ?? '—'}
                          </p>
                          <p className="text-xs text-on-surface-variant">
                            {r.party_size} personnes
                            {r.table_number != null ? ` · Table ${r.table_number}` : ''}
                          </p>
                        </div>
                        <span className="text-sm tabular-nums text-on-surface">
                          {formatTime(r.reservation_at)}
                        </span>
                      </div>
                      {status !== 'cancelled' && status !== 'no_show' && (
                        <div className="grid grid-cols-3 gap-2">
                          {status !== 'confirmed' && status !== 'seated' && (
                            <button
                              type="button"
                              disabled={busy === r.id}
                              onClick={() => void updateStatus(r.id, 'confirmed')}
                              className="min-h-[44px] text-[11px] font-semibold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-50"
                            >
                              Confirmer
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busy === r.id}
                            onClick={() => void updateStatus(r.id, 'cancelled')}
                            className="min-h-[44px] text-[11px] font-semibold uppercase tracking-wider text-on-surface bg-surface disabled:opacity-50"
                            style={{ border: '0.5px solid #000' }}
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            disabled={busy === r.id}
                            onClick={() => void updateStatus(r.id, 'no_show')}
                            className="min-h-[44px] text-[11px] font-semibold uppercase tracking-wider text-on-surface bg-surface disabled:opacity-50"
                            style={{ border: '0.5px solid #000' }}
                          >
                            No-show
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
